// src/Login.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { DEFAULT_ORG_ID, getOrgBrandAssets } from "./utils/brandAssets";
import {
  locationToAuthReturnTarget,
  readStoredAuthReturnTarget,
  storeAuthReturnTarget,
} from "./utils/authReturnTarget";

const DARK_KEY = "darkMode";
const PLACEHOLDER_PROJECT_RE = /your_project/i;

function resolveFunctionsBaseUrl(projectId) {
  const envBase = String(process.env.REACT_APP_FUNCTIONS_BASE_URL || "").trim();
  if (envBase && !PLACEHOLDER_PROJECT_RE.test(envBase)) {
    if (projectId && envBase.includes(`africa-south1-${projectId}.cloudfunctions.net`)) {
      return envBase
        .replace(`africa-south1-${projectId}.cloudfunctions.net`, `europe-west1-${projectId}.cloudfunctions.net`)
        .replace(/\/+$/, "");
    }
    return envBase.replace(/\/+$/, "");
  }
  return projectId ? `https://europe-west1-${projectId}.cloudfunctions.net` : "";
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, loginWithGoogle } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetStatus, setResetStatus] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const stateReturnTarget = useMemo(
    () => locationToAuthReturnTarget(location?.state?.from || {}),
    [location?.state]
  );
  const returnTarget = stateReturnTarget || readStoredAuthReturnTarget(
    typeof window === "undefined" ? null : window.sessionStorage
  ) || "/dashboard";

  useEffect(() => {
    if (!stateReturnTarget || typeof window === "undefined") return;
    storeAuthReturnTarget(window.sessionStorage, stateReturnTarget);
  }, [stateReturnTarget]);

  useEffect(() => {
    if (!user?.uid || typeof window === "undefined") return;
    const storedTarget = readStoredAuthReturnTarget(window.sessionStorage);
    if (!storedTarget) return;
    storeAuthReturnTarget(window.sessionStorage, "");
    navigate(storedTarget, { replace: true });
  }, [navigate, user?.uid]);

  // Respect theme preference
  const isDark = useMemo(() => localStorage.getItem(DARK_KEY) === "true", []);
  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    document.body.classList.toggle("dark", isDark);
  }, [isDark]);

  const assets = getOrgBrandAssets(DEFAULT_ORG_ID, isDark ? "dark" : "light");
  const hero = isDark ? "/brand/nia/nia-login-dark.jpg" : "/brand/nia/nia-login-light.jpg";
  const withTimeout = async (promise, ms = 25000) => {
    let timeoutId;
    const timeout = new Promise((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error("timeout")), ms);
    });
    try {
      return await Promise.race([promise, timeout]);
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) return;
    try {
      setLoading(true);
      await withTimeout(login(email.trim(), password), 25000);
      try { sessionStorage.setItem("nia_post_login_hint", "1"); } catch {}
      if (typeof window !== "undefined") {
        storeAuthReturnTarget(window.sessionStorage, "");
      }
      navigate(returnTarget, { replace: true });
    } catch (err) {
      if (err?.message === "timeout") {
        setError("Login is taking too long. Please check your connection and try again.");
      } else {
        setError("Unable to sign in. Check your credentials.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    try {
      setLoading(true);
      if (typeof window !== "undefined") {
        storeAuthReturnTarget(window.sessionStorage, returnTarget);
      }
      const result = await withTimeout(loginWithGoogle(), 25000);
      if (result?.redirect) return;
      try { sessionStorage.setItem("nia_post_login_hint", "1"); } catch {}
      if (typeof window !== "undefined") {
        storeAuthReturnTarget(window.sessionStorage, "");
      }
      navigate(returnTarget, { replace: true });
    } catch (err) {
      if (err?.message === "timeout") {
        setError("Google sign-in is taking too long. Please try again.");
      } else {
        setError("Google sign-in failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setResetStatus("");
    if (!email.trim()) {
      setResetStatus("Enter your email first, then use Reset password.");
      return;
    }
    try {
      setResetLoading(true);
      const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || "";
      const base = resolveFunctionsBaseUrl(projectId);
      if (!base) {
        throw new Error("missing_functions_base");
      }
      const endpoint = `${base}/sendPasswordReset`;
      const response = await withTimeout(fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          loginUrl: `${window.location.origin}/login`,
        }),
      }), 25000);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "reset_failed");
      }
      setResetStatus("Password reset email sent. Check your inbox and spam folder.");
    } catch (err) {
      if (err?.message === "timeout") {
        setResetStatus("Reset email is taking too long. Please try again.");
      } else {
        setResetStatus("Unable to send reset email. Check the email address and try again.");
      }
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Splash (left) */}
      <div className="login-hero" style={{ backgroundImage: `url(${hero})` }}>
        {/* brand claim only (no logo on the left) */}
        <div className="login-claim">
          <h1 className="login-claim-title">Nia COACH — NEUROGRES™ Coach</h1>
          <p className="login-claim-sub">
            Your real-time guide to resilience, focus, and human performance.
          </p>
        </div>
      </div>

      {/* Login form (right) */}
      <div className="login-panel">
        <form className="login-card" onSubmit={handleLogin} autoComplete="off">
          {/* big emblem, left-aligned with the text */}
          <div className="brand-block">
            <img
              src={assets.wordmarkUrl}
              alt="Nia COACH"
              className="brand-wordmark"
            />
          </div>

          <h2 className="login-title">Welcome to Nia COACH</h2>
          <p className="login-sub">Log in to begin your session</p>
          <div className="nia-alert" style={{ margin: "0 0 12px 0", fontSize: 13, padding: "10px 12px" }}>
            If you were provisioned by an admin, use your temporary password, then update it from your account after login.
          </div>

          <input
            className="form-input"
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ marginBottom: 10 }}
            autoFocus
          />

          <div style={{ position: "relative" }}>
            <input
              className="form-input"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ marginBottom: 10, paddingRight: 36 }}
              autoComplete="current-password"
            />
            <span
              onClick={() => setShowPassword((v) => !v)}
              style={{
                position: "absolute",
                top: 9,
                right: 12,
                cursor: "pointer",
                userSelect: "none",
                opacity: 0.7,
              }}
              title={showPassword ? "Hide" : "Show"}
            >
              {showPassword ? "🙈" : "👁️"}
            </span>
          </div>

          <div style={{ marginTop: 4, marginBottom: 8, fontSize: 13 }}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleResetPassword}
              disabled={loading || resetLoading}
              style={{ padding: 0, border: "none", background: "transparent", textDecoration: "underline" }}
            >
              {resetLoading ? "Sending reset email..." : "Forgot password? Reset it by email"}
            </button>
          </div>
          {resetStatus && (
            <div style={{ color: "var(--ui-muted)", fontSize: 13, marginBottom: 6 }}>
              {resetStatus}
            </div>
          )}

          {error && (
            <div style={{ color: "var(--color-danger)", fontSize: 13, marginBottom: 8 }}>
              {error}
            </div>
          )}

          <button
            className="btn btn-primary"
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              margin: "12px 0 0 0",
              fontWeight: 700,
              fontSize: "1.12rem",
              borderRadius: 6,
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Signing in…" : "Log in"}
          </button>

          <button
            className="btn btn-ghost"
            type="button"
            onClick={handleGoogle}
            disabled={loading}
            style={{ width: "100%", marginTop: 10 }}
          >
            Continue with Google
          </button>

          <div style={{ marginTop: 12, fontSize: 13, textAlign: "center" }}>
            New here?{" "}
            <a href="/signup" style={{ fontWeight: 600 }}>
              Create an account
            </a>
          </div>

          <div className="login-meta" style={{ marginTop: 18, color: "var(--ui-muted)", fontSize: 13 }}>
            By logging in you accept our terms.
            <span style={{ float: "right", fontWeight: 500, fontSize: 12 }}>v1.0</span>
          </div>
        </form>
      </div>
    </div>
  );
}
